<template>
  <div ref="componentRoot" class="d-flex flex-column gap-4 mt-5">
    <div>
      <h3 class="mb-4 d-flex align-center">Rechnungsadresse</h3>
      <v-divider class="mb-4"></v-divider>
      <v-row dense>
        <v-col
          v-if="mainOrder.companyName"
          cols="12"
          sm="6"
          class="d-flex align-center"
        >
          <span><b>Firmenname:</b> {{ mainOrder.companyName }}</span>
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span><b>Name:</b> {{ mainOrder.name }}</span>
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span><b>Adresse:</b> {{ mainOrder.address }}</span>
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span
            ><b>PLZ / Ort:</b> {{ mainOrder.postcode }}
            {{ mainOrder.place }}</span
          >
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span><b>Telefon:</b> {{ mainOrder.phoneNumber }}</span>
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span><b>E-Mail:</b> {{ mainOrder.mail }}</span>
        </v-col>
      </v-row>
    </div>
    <div v-if="showDeliveryAddress">
      <h3 class="mb-4 mt-4 d-flex align-center">Lieferadresse</h3>
      <v-divider class="mb-4"></v-divider>
      <v-row dense>
        <v-col
          v-if="mainOrder.deliveryAddress.companyName"
          cols="12"
          sm="6"
          class="d-flex align-center"
        >
          <span
            ><b>Firmenname:</b>
            {{ mainOrder.deliveryAddress.companyName }}</span
          >
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span><b>Name:</b> {{ mainOrder.deliveryAddress.name }}</span>
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span><b>Adresse:</b> {{ mainOrder.deliveryAddress.address }}</span>
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span
            ><b>PLZ / Ort:</b> {{ mainOrder.deliveryAddress.postcode }}
            {{ mainOrder.deliveryAddress.place }}</span
          >
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span
            ><b>Telefon:</b> {{ mainOrder.deliveryAddress.phoneNumber }}</span
          >
        </v-col>
        <v-col cols="12" sm="6" class="d-flex align-center">
          <span><b>E-Mail:</b> {{ mainOrder.deliveryAddress.mail }}</span>
        </v-col>
      </v-row>
    </div>
    <div>
      <h3 class="mb-4 mt-4">Bestellungen</h3>
      <v-divider class="mb-4"></v-divider>
      <v-table density="comfortable">
        <thead>
          <tr>
            <th class="text-left">Gemeinde</th>
            <th class="text-left">Adressobjekt</th>
            <th class="text-left">Grundstücks-/Katasternummer</th>
            <th class="text-left">Produkt</th>
            <th class="text-left">Medium</th>
            <th class="text-left">Menge</th>
            <th class="text-left">Preis</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(product, index) in mergedProducts" :key="index">
            <td>{{ product.communityName }}</td>
            <td>{{ product.address || 'Unbekannt' }}</td>
            <td>{{ product.parcel || 'Unbekannt' }}</td>
            <td>
              <b>{{ product.groupName }}</b>
              <br />{{ product.name }}
            </td>
            <td>{{ capitalizeFirstLetter(product.type) }}</td>
            <td>{{ product.quantity }}</td>
            <td>
              <span :class="{ 'text-primary': product.price === 0 }">
                {{
                  product.price === 0
                    ? 'Ab 3 Werkleitungsdaten kostenlos'
                    : `${product.price}.-`
                }}
              </span>
            </td>
          </tr>
          <tr>
            <td colspan="6" class="text-right"><b>Grundpauschale:</b></td>
            <td>50.00</td>
          </tr>
          <tr>
            <td colspan="6" class="text-right"><b>Zwischensumme:</b></td>
            <td>{{ subtotalAmount.toFixed(2) }}</td>
          </tr>
          <tr>
            <td colspan="6" class="text-right"><b>MwSt. (8,1%):</b></td>
            <td>{{ vatAmount.toFixed(2) }}</td>
          </tr>
          <tr>
            <td colspan="6" class="text-right"><b>Gesamtbetrag:</b></td>
            <td>{{ totalAmount.toFixed(2) }} *</td>
          </tr>
        </tbody>
      </v-table>
      <p class="mt-2 mb-4 text-caption">
        * Die angegebenen Preise bilden die Standardbestellung ab und werden bei
        Grössenabweichungen der Pläne angepasst. Die Preise sind ohne Gewähr.
        Die Rechnung folgt mit der Zustellung der Papierpläne oder separat nach
        Auslieferung der digitalen Pläne.
      </p>
    </div>
    <div class="mb-4">
      <h3 class="mb-4">Bemerkungen</h3>
      <p>{{ mainOrder.comments || 'Keine Bemerkungen angegeben' }}</p>
    </div>
    <div class="mb-4" v-if="uploadedFiles.length">
      <v-divider class="mb-4"></v-divider>
      <h3 class="mb-4">Hochgeladene Dokumente</h3>
      <v-list>
        <v-list-item v-for="(file, index) in uploadedFiles" :key="index">
          <v-list-item-content>
            <div class="d-flex align-center">
              <v-icon :color="'primary'" class="mr-2">
                {{ getFileIcon(file.name) }}
              </v-icon>
              <span>{{ file.name }}</span>
            </div>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </div>
    <div class="checkbox-container">
      <v-checkbox
        v-model="conditionsAccepted"
        :rules="[
          (v) => !!v || 'Bitte Datenschutzinformationen zur Kenntnis nehmen.',
        ]"
        color="primary"
        @change="emitConditionsAccepted"
      >
        <template #label>
          Ich nehme die &nbsp;
          <a
            href="https://www.ingesa.ch/datenschutz"
            target="_blank"
            rel="noopener"
            class="text-primary font-weight-medium"
            style="text-decoration: underline"
          >
            Datenschutzinformationen
          </a>
          &nbsp; der Ingesa zur Kenntnis.
        </template>
      </v-checkbox>
    </div>
    <div class="captcha-container">
      <vue-hcaptcha
        sitekey="56986226-0fda-46ac-8652-a7a8d9b64f44"
        @verify="onVerify"
        @expire="onExpire"
        @error="onError"
      />
    </div>
  </div>
</template>

<script setup>
import {
  defineProps,
  defineEmits,
  ref,
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
  defineExpose,
} from 'vue'
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha'

const debounce = (func, wait) => {
  let timeout
  return function (...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(this, args), wait)
  }
}

const props = defineProps({
  mainOrder: Object,
  orders: Array,
  communities: Array,
  conditionsAccepted: Boolean,
  hCaptchaAccepted: Boolean,
})

const emit = defineEmits([
  'update:conditionsAccepted',
  'update:hCaptchaAccepted',
  'update:hCaptchaToken',
  'heightChange',
])
const componentRoot = ref(null)

const sendHeightUpdate = debounce(() => {
  const scrollHeight = componentRoot.value.scrollHeight
  const docHeight = document.documentElement.scrollHeight
  const sendingHeight = Math.max(scrollHeight, docHeight)
  emit('heightChange', sendingHeight)
}, 300)

defineExpose({
  sendHeightUpdate,
})

const setupResizeObserver = () => {
  const resizeObserver = new ResizeObserver(() => {
    sendHeightUpdate()
  })

  onMounted(() => {
    resizeObserver.observe(document.documentElement)
  })

  onBeforeUnmount(() => {
    resizeObserver.disconnect()
  })
}

const conditionsAccepted = ref(props.conditionsAccepted || false)
const hCaptchaAccepted = ref(props.hCaptchaAccepted || false)

watch(
  () => props.conditionsAccepted,
  (val) => {
    conditionsAccepted.value = val
  },
)

watch(
  () => props.hCaptchaAccepted,
  (val) => {
    hCaptchaAccepted.value = val
  },
)

const emitConditionsAccepted = () => {
  emit('update:conditionsAccepted', conditionsAccepted.value)
}

const getCommunityName = (nameOrId) => {
  if (!props.communities) return nameOrId
  const found = props.communities.find(
    (c) => c.id === nameOrId || c.name === nameOrId,
  )
  return found?.name || nameOrId
}

const showDeliveryAddress = computed(() => {
  if (!props.mainOrder.useDifferentDelivery) return false
  const m = props.mainOrder
  const d = props.mainOrder.deliveryAddress
  return (
    d.name !== m.name ||
    d.address !== m.address ||
    d.postcode !== m.postcode ||
    d.place !== m.place
  )
})

const mergedProducts = computed(() => {
  return props.orders.flatMap((order) =>
    sortedSelectedProducts(order.selectedProducts).map((product) => ({
      ...product,
      communityName: getCommunityName(order.community.name),
      parcel: order.orderParcel,
      address: order.orderAddress,
    })),
  )
})

const sortedSelectedProducts = (selectedProducts) => {
  if (!selectedProducts) return []
  return selectedProducts
    .filter((product) => product.type !== 'grundpauschale')
    .sort((a, b) => {
      if (a.price === 0 && b.price !== 0) return 1
      if (a.price !== 0 && b.price === 0) return -1
      return a.groupName.localeCompare(b.groupName)
    })
}

const capitalizeFirstLetter = (string) => {
  return string === 'papier'
    ? string.charAt(0).toUpperCase() + string.slice(1)
    : string.toUpperCase()
}

const subtotalAmount = computed(() => {
  return props.orders.reduce((sum, order) => {
    const orderSum = order.selectedProducts?.reduce(
      (s, p) => s + (Number(p.price) || 0),
      0,
    )
    return sum + orderSum
  }, 0)
})

const vatAmount = computed(() => {
  return roundToNearestFiveRappen(subtotalAmount.value * 0.081)
})

const totalAmount = computed(() => {
  return roundToNearestFiveRappen(subtotalAmount.value + vatAmount.value)
})

const getFileIcon = (filename) => {
  if (filename.endsWith('.pdf')) return 'mdi-file-pdf'
  if (filename.endsWith('.png')) return 'mdi-file-image'
  if (filename.endsWith('.jpg') || filename.endsWith('.jpeg'))
    return 'mdi-file-image'
  if (filename.endsWith('.dxf')) return 'mdi-file'
  return 'mdi-file'
}

const uploadedFiles = computed(() => {
  if (!Array.isArray(props.orders)) {
    console.error('Expected props.orders to be an array', props.orders)
    return []
  }
  return props.orders.flatMap((order) => {
    if (!order || !order.files) {
      return []
    }
    return order.files
  })
})

const onVerify = (token) => {
  hCaptchaAccepted.value = true
  emit('update:hCaptchaAccepted', hCaptchaAccepted.value)
  emit('update:hCaptchaToken', token)
  sendHeightUpdate()
}

const onExpire = () => {
  console.log('Captcha abgelaufen')
  hCaptchaAccepted.value = false
  emit('update:hCaptchaAccepted', hCaptchaAccepted.value)
  sendHeightUpdate()
}

const onError = (error) => {
  console.error('Captcha-Fehler:', error)
  hCaptchaAccepted.value = false
  emit('update:hCaptchaAccepted', hCaptchaAccepted.value)
  sendHeightUpdate()
}

const roundToNearestFiveRappen = (amount) => {
  let numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  return Math.round(numAmount * 20) / 20
}

// Initialize ResizeObserver and send initial height
onMounted(() => {
  setupResizeObserver()
  sendHeightUpdate()
})

// Watch for changes in conditionsAccepted and hCaptchaAccepted
watch([conditionsAccepted, hCaptchaAccepted], () => {
  sendHeightUpdate()
})
</script>

<style scoped>
a.text-primary:hover {
  text-decoration: none !important;
}
.captcha-container {
  display: flex;
  justify-content: center;
  margin: 10px 0;
}
.text-primary {
  color: red;
}
@media (max-width: 768px) {
  .checkbox-container {
    flex-direction: column;
    align-items: flex-start;
    padding: 0 10px;
  }
  .captcha-container {
    padding: 0 10px;
  }
}
</style>
